// ============================================
// 인텐션 메타 — aiGuide.js에서 책임 분리 (순수 이동)
// ============================================
// 인텐션 로컬 모드 헬퍼 + 런타임 메타 가드(visualizationOnly/autoFallback/defaultPeriod override).
// 의존: aiDateResolver(resolveDefaultPeriod/USER_TIME_KW_RE) + aiPrompts(LOCAL_INTENTIONS_DATA).

import { resolveDefaultPeriod, USER_TIME_KW_RE } from './aiDateResolver';
import { LOCAL_INTENTIONS_DATA } from './aiPrompts';

// ============================================
// 인텐션 로컬 모드 헬퍼 — aiPrompts.js의 LOCAL_INTENTIONS_DATA에서 backend 응답 형식 재현
// ============================================
// USE_LOCAL_INTENTIONS=true 환경에서 get_intentions 도구가 backend hop 없이 메타를 즉시 반환

// snake_case row → backend IntentionItemDTO 동등 (camelCase) 변환
function _toIntentionDto(row) {
  return {
    intentionKey: row.intention_key,
    endpoint: row.endpoint,
    service: row.service,
    description: row.description,
    params: row.params,
    creative: !!row.creative,
    priorEndpoint: row.prior_endpoint,
    availableFields: row.available_fields,
    callApiGuide: row.call_api_guide,
    ...(row.visualizationOnly === true && { visualizationOnly: true }),
    ...(typeof row.fallback_intention === 'string' && row.fallback_intention && { fallback_intention: row.fallback_intention }),
    ...(row.autoFallback === true && { autoFallback: true }),
    ...(row.forceParams === true && { forceParams: true }),
    ...(row.defaultPeriod && { defaultPeriod: row.defaultPeriod }),
    ...(typeof row.defaultDisplay === 'string' && row.defaultDisplay && { defaultDisplay: row.defaultDisplay }),
    ...(typeof row.primaryRateKey === 'string' && row.primaryRateKey && { primaryRateKey: row.primaryRateKey }),
    // [RANKING_SORT_GUARD] default_sort / tie_break_field 전파 — call_api 후 자동 정렬 + 동률 tie-break (executeCallApi + applySort에서 활용)
    ...(row.default_sort && typeof row.default_sort === 'object' && { default_sort: row.default_sort }),
    ...(typeof row.tie_break_field === 'string' && row.tie_break_field && { tie_break_field: row.tie_break_field }),
  };
}

// backend API2AiChatService.getIntentionsByCategory의 응답 구조 재현 —
// 카테고리 인텐션 + priorEndpoint 참조 인텐션 cross-category 보강
// (categoryRules는 omo refactor B-frontend로 폐기 — DISPLAY_GUIDE + intention.description으로 분산)
export function getLocalIntentionsByCategory(category) {
  const upperCategory = String(category || '').toUpperCase();
  const categoryItems = LOCAL_INTENTIONS_DATA.intentions.filter(i => i.category === upperCategory);
  if (categoryItems.length === 0) return null;

  const intentions = [...categoryItems];

  // priorEndpoint 가 다른 카테고리 인텐션을 가리키면 자동 보강 (backend missingPriors 흐름)
  const currentKeys = new Set(intentions.map(i => i.intention_key));
  const missingPriors = new Set(
    intentions
      .map(i => i.prior_endpoint)
      .filter(p => p && !currentKeys.has(p))
  );
  if (missingPriors.size > 0) {
    for (const it of LOCAL_INTENTIONS_DATA.intentions) {
      if (missingPriors.has(it.intention_key)) intentions.push(it);
    }
  }

  return {
    category: upperCategory,
    totalCount: intentions.length,
    intentions: intentions.map(_toIntentionDto),
    rules: [],  // backward compat — consumer destructure 안전성. 실제 rules 폐기됨.
  };
}

// intention metadata에 visualizationOnly:true 인 경우 dedicated visualization tool 정보 반환
// show_display 가드에 사용 — show_display로 처리하면 안 되는 intention 검출
// call_api_guide에서 show_* 도구 이름 자동 추출 (intention key hardcoding 없음)
export function checkVisualizationOnlyConflict(intentionMeta) {
  if (!intentionMeta) return null;
  if (intentionMeta.visualizationOnly !== true) return null;
  const guide = intentionMeta.callApiGuide || intentionMeta.call_api_guide || '';
  const match = guide.match(/show_[a-z_]+/i);
  return {
    visualizationTool: match ? match[0] : 'the dedicated visualization tool',
    intentionKey: intentionMeta.intentionKey || intentionMeta.intention_key,
    callApiGuide: guide,
  };
}

// intention metadata에 autoFallback:true + defaultPeriod 정의된 경우
// date params (startDate/endDate/standardYear)를 defaultPeriod 토큰 resolve로 자동 채움
// param format spec (e.g. "YYYY-MM-DDTHH:mm:ss") 따라 ISO datetime suffix 부착
//   - 매핑: startDate → resolved.startDate, endDate → resolved.endDate, standardYear → resolved.startDate
// forceParams:true 시 LLM이 보낸 args 무시하고 defaultPeriod 강제 override
//   - "현재 기준" 결정성이 본질인 intention (예: LTV 시각화)에 사용
//   - 일반 RANGE intention은 forceParams 없이 missing 시만 fallback (LLM 의도 우선)
// forceParams 미정의 또는 false 시 기존 동작: missing param만 채움 (idempotent)
//
// [L1-MEMBERSHIP-007 generic extension] required:false date params 도 missing 시 fallback 적용.
//   Why: LLM이 stochastic하게 startDate/endDate 생략 → backend 'all-time' aggregate 반환 →
//        run마다 displayCount 변동 (예: 9 vs 12) + 누적 raw counts vs 기간 필터 counts 혼동.
//   Generic rule: DATE_PARAM_MAP key (startDate/endDate/standardYear) 인 param 은 required 여부와 무관하게 missing 시 채움.
//   영향 범위: autoFallback:true 인 intention만. 기존 autoFallback:true intention은 전부 required:true date param 사용 → 동작 변화 없음.
export function applyAutoFallbackParams(intentionMeta, params, today = new Date()) {
  if (!intentionMeta?.autoFallback) return params;
  if (!intentionMeta?.defaultPeriod) return params;

  const paramsSchema = typeof intentionMeta.params === 'string'
    ? JSON.parse(intentionMeta.params || '[]')
    : (intentionMeta.params || []);

  const resolved = resolveDefaultPeriod(intentionMeta, today);
  if (!resolved) return params;

  const DATE_PARAM_MAP = {
    startDate: resolved.startDate,
    endDate: resolved.endDate,
    standardYear: resolved.startDate,
  };

  const force = intentionMeta.forceParams === true;
  const next = { ...(params || {}) };
  for (const p of paramsSchema) {
    // date param (startDate/endDate/standardYear) 은 required 여부와 무관하게 처리
    // 그 외 param은 기존대로 required:true 만 처리
    const isDateParam = Object.prototype.hasOwnProperty.call(DATE_PARAM_MAP, p.name);
    if (!p.required && !isDateParam) continue;
    const needsIsoSuffix = typeof p.format === 'string' && /T.*[Hh]+.*[mM]+.*[sS]+/.test(p.format);
    if (!force && next[p.name]) {
      // [P1-2 anchor 6 extension] LLM이 date-only("2026-03-01")로 보내고 backend는 LocalDateTime ISO format 기대 시 mismatch → HTTP 400
      //   param format spec이 datetime이고 현재 값이 date-only면 ISO suffix 자동 부착 (LLM 의도 date 자체는 유지)
      if (needsIsoSuffix && typeof next[p.name] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(next[p.name])) {
        next[p.name] = `${next[p.name]}T00:00:00`;
      }
      continue;
    }
    const raw = DATE_PARAM_MAP[p.name];
    if (!raw) continue;
    next[p.name] = needsIsoSuffix ? `${raw}T00:00:00` : raw;
  }
  return next;
}

// [L1-MEMBERSHIP-007 STAT_RANKING_PERIOD_INCONSISTENCY] generic — defaultPeriod 강제 override (사용자 input no time kw 시)
//   본질: 사용자 명시 시점 없을 때 LLM이 stochastic하게 dateRange 선택
//        - 일부 run: dates 생략 (→ applyAutoFallbackParams로 defaultPeriod 채움)
//        - 일부 run: "today~today" 등 자체 추측 (→ 잘못된 narrow range, single day 데이터)
//   해소: 사용자 input이 explicit time keyword 미포함 + intention에 defaultPeriod 정의 시,
//        LLM이 보낸 dates 무시하고 defaultPeriod 강제 적용 (LLM hallucination 차단).
//   결정성: 모든 run에서 동일 defaultPeriod 사용 → answer determinism 확보.
//   범용 — 어느 intention이든 defaultPeriod 정의 + 사용자 time kw 없으면 동작. case-specific hardcode X.
//   회귀 risk: 사용자가 명시 time keyword 입력 시 미발동 (LLM dates 보존).
//   forceParams:true intention은 applyAutoFallbackParams에서 이미 처리 — 본 함수 진입 시 변동 없음 (idempotent).
export function applyDefaultPeriodOverride(intentionMeta, params, userInput, today = new Date()) {
  if (!intentionMeta?.defaultPeriod) return params;
  if (intentionMeta.forceParams === true) return params;  // forceParams는 applyAutoFallbackParams에서 이미 처리
  if (!userInput || typeof userInput !== 'string') return params;
  if (USER_TIME_KW_RE.test(userInput)) return params;  // 사용자 명시 time kw 있으면 LLM dates 보존

  const paramsSchema = typeof intentionMeta.params === 'string'
    ? JSON.parse(intentionMeta.params || '[]')
    : (intentionMeta.params || []);

  const resolved = resolveDefaultPeriod(intentionMeta, today);
  if (!resolved) return params;

  const DATE_PARAM_MAP = {
    startDate: resolved.startDate,
    endDate: resolved.endDate,
    standardYear: resolved.startDate,
  };

  const next = { ...(params || {}) };
  let changed = false;
  for (const p of paramsSchema) {
    const isDateParam = Object.prototype.hasOwnProperty.call(DATE_PARAM_MAP, p.name);
    if (!isDateParam) continue;
    const raw = DATE_PARAM_MAP[p.name];
    if (!raw) continue;
    const needsIsoSuffix = typeof p.format === 'string' && /T.*[Hh]+.*[mM]+.*[sS]+/.test(p.format);
    const target = needsIsoSuffix ? `${raw}T00:00:00` : raw;
    if (next[p.name] !== target) {
      next[p.name] = target;
      changed = true;
    }
  }
  return changed ? next : params;
}
